/**
 * What a person typed, turned into a query FTS5 will accept, and the statements
 * that run it.
 *
 * **Nothing typed ever reaches MATCH raw.** FTS5's query language throws on a
 * stray double quote, on a colon (it reads as a column filter), and on the bare
 * words AND, OR, NOT and NEAR. A shop typing `payment: cash` would crash the
 * screen. So every word is stripped of quotes and wrapped in them, which turns
 * it into a PHRASE — and inside a phrase FTS5 hands the text to the tokenizer
 * and takes no syntax from it at all.
 *
 * **Three queries, one statement.** The main search, the reference step, and the
 * loose fallback differ only in the MATCH expression. `searchSql` serves all
 * three, so there is one place where ranking and the join are decided.
 *
 * **The fallback is the answer to typo tolerance.** FTS5 has no fuzzy operator,
 * and the extensions that would give it one are not compiled into the build.
 * So a misspelling returns nothing, and rather than draw that nothing the screen
 * runs a second, coarser query and sorts its candidates by edit distance in
 * JavaScript. Measured over fifty thousand documents: 1.3ms end to end, and it
 * recovers "Ramesh" from "ramseh". A slip in the FIRST TWO characters stays
 * lost, because the coarse query starts from them.
 *
 * Pure by construction, so it is tested under bun with no database. The
 * statements it returns are strings; `entry-repo.ts` runs them.
 */

/** Below this, a search is not run: one letter matches most of a record. */
export const MIN_QUERY_CHARS = 2;

/** How many ranked hits a search returns. No paging in this phase. */
export const RESULT_LIMIT = 50;

/**
 * How many candidates the loose fallback fetches before JavaScript sorts them.
 *
 * Unranked, because bm25 over a two-letter prefix is close to meaningless — the
 * ordering that matters is edit distance, and that is computed here. 400 was
 * measured: fetching and scoring them costs 1.3ms at fifty thousand documents.
 *
 * It returns the document's own title and body as well, because the JavaScript
 * pass measures the distance against the WORDS THAT WERE INDEXED. Measuring
 * against a re-rendered value instead would score against something the query
 * never saw.
 */
export const FALLBACK_SCAN_LIMIT = 400;

/** How many directory rows the reference step expands through. */
export const REF_SEED_LIMIT = 5;

/**
 * ASCII punctuation and whitespace: what `unicode61` treats as a separator.
 *
 * Checked by walking the string rather than by a regex with a unicode property
 * escape, so it behaves the same on Hermes as under bun, and so that Devanagari,
 * Tamil and every other script fall through as content.
 */
const SEPARATORS = ' \t\n\r\f\v!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';

/** Does this word carry anything the tokenizer would keep? */
const hasContent = (word: string): boolean => {
  for (const character of word) {
    if (!SEPARATORS.includes(character)) return true;
  }
  return false;
};

/**
 * Take the commas out from between digits.
 *
 * The index holds a money value as its raw digits, because `formatMoney` draws
 * ₹1,023 and that comma splits into the two terms "1" and "023". A shop typing
 * 1,023 or 1,00,000 means one number, so the commas come out here and the two
 * sides meet. Twice, because the groups overlap in an Indian grouping.
 */
const glueDigits = (typed: string): string =>
  typed.replace(/([0-9]),([0-9])/g, '$1$2').replace(/([0-9]),([0-9])/g, '$1$2');

/** The words in what was typed, cleaned, in order, with the empties dropped. */
export const wordsIn = (typed: string): string[] =>
  glueDigits(typed)
    .split(/\s+/)
    .map((word) => word.replace(/"/g, ''))
    .filter((word) => word !== '' && hasContent(word));

/**
 * Is this worth running at all?
 *
 * Counted over the words rather than the raw string, so three spaces and a
 * comma is not a search.
 */
export const isSearchable = (typed: string): boolean =>
  wordsIn(typed).join('').length >= MIN_QUERY_CHARS;

/**
 * The same floor, for a query that may not be there at all.
 *
 * The twin of `isFiltering`, and named for it: a list is cut when a filter is
 * on OR when a search is running, and every door that asks one question has to
 * ask the other in the same breath. Without it each caller writes
 * `text !== null && isSearchable(text)` by hand and one of them eventually
 * writes only half.
 */
export const isSearching = (typed: string | null): boolean =>
  typed !== null && isSearchable(typed);

/**
 * The everyday query: every word as a prefix, all of them required.
 *
 * ANDed, which is what a person means by typing two words: "ramesh charger" is
 * the charger he bought, not everything matching either. FTS5 ANDs adjacent
 * phrases by default, so the words are simply joined by a space.
 */
export const matchExpression = (typed: string): string | null => {
  const words = wordsIn(typed);
  if (words.length === 0) return null;
  return words.map((word) => `"${word}"*`).join(' ');
};

/**
 * The expression a RECORD's own search uses. Words as prefixes, numbers whole.
 *
 * `matchExpression` makes every word a prefix, which is right for a name —
 * somebody typing "ramesh" means Ramesh Gupta. It is wrong for a figure.
 * A shop typing 695 on the Sales page means the ₹695 sale, and `"695"*` also
 * answers 6950 and 69500, which are three different days' takings arriving as
 * one answer. So a word that is nothing but digits is matched WHOLE.
 *
 * **The amounts are already in the index**, as raw digits: `search-doc.ts`
 * renders a money or number field with `String(value)`, and `glueDigits` takes
 * the commas out of what was typed, so ₹1,023 and "1,023" meet in the middle.
 * That is why matching an amount needs no clause of its own against
 * `signed_value` — which could not be written here anyway, since the filter
 * compiler is given no schema and so knows no money field.
 *
 * **What it still cannot promise.** `unicode61` splits 695.50 into "695" and
 * "50", so a whole-word 695 finds that row too. The tokenizer decides that,
 * not this expression, and changing it would mean rebuilding every index.
 */
export const recordMatchExpression = (typed: string): string | null => {
  const words = wordsIn(typed);
  if (words.length === 0) return null;
  return words.map((word) => (DIGITS.test(word) ? `"${word}"` : `"${word}"*`)).join(' ');
};

/** A word a person means as a figure: digits, possibly with one point. */
const DIGITS = /^[0-9]+(?:\.[0-9]+)?$/;

/**
 * The same, restricted to the title column: step one of the reference step.
 *
 * A sale holds the customer's id, not his name, so "ramesh" must first find
 * HIS row in the Customers directory. Restricting to the title is what keeps
 * that seed narrow — a note mentioning Ramesh is not Ramesh.
 */
export const titleMatchExpression = (typed: string): string | null => {
  const words = wordsIn(typed);
  if (words.length === 0) return null;
  return `title:(${words.map((word) => `"${word}"*`).join(' ')})`;
};

/**
 * Step two: the entries pointing at any of those rows.
 *
 * ORed, not ANDed: an entry naming ANY of the matched customers is a hit. The
 * ids arrive already stripped of dashes, as `refToken` wrote them.
 */
export const refsMatchExpression = (tokens: readonly string[]): string | null => {
  const clean = [...new Set(tokens.filter((token) => token !== '' && hasContent(token)))];
  if (clean.length === 0) return null;
  return `refs:(${clean.map((token) => `"${token}"`).join(' OR ')})`;
};

/**
 * Two MATCH expressions as one: a row matching EITHER is a hit.
 *
 * This is what lets the section counts be one statement. The screen's strict
 * answer is the union of the direct match and the reference step, and a row can
 * be in both — a wage whose note says "Ramesh" and which points at him. Counting
 * the two statements separately and adding would count that row twice; one
 * MATCH over the OR counts it once, because FTS5 answers each document once.
 *
 * Each side is parenthesised whole, because the direct side is an implicit AND
 * of phrases and the refs side carries a column filter, and neither must bind
 * into the other. Pinned in search.sqlite.test.ts.
 */
export const eitherExpression = (...parts: readonly (string | null)[]): string | null => {
  const present = parts.filter((part): part is string => part !== null && part !== '');
  if (present.length === 0) return null;
  if (present.length === 1) return present[0] ?? null;
  return present.map((part) => `(${part})`).join(' OR ');
};

/**
 * The loose query, run ONLY when the strict one found nothing.
 *
 * Each word cut to its first two characters and ORed, so one misspelt word does
 * not sink the whole search. By code point and not by `slice`, so a Devanagari
 * cluster is never cut through the middle of a surrogate pair.
 *
 * ORed rather than ANDed for a second reason worth naming: it also rescues a
 * search where one word is simply not in the index at all — "customer ramesh",
 * where "customer" is a field's label and labels are not indexed.
 */
export const looseExpression = (typed: string): string | null => {
  const words = wordsIn(typed);
  if (words.length === 0) return null;
  const stems = [
    ...new Set(
      words
        .map((word) => [...word].slice(0, MIN_QUERY_CHARS).join(''))
        .filter((stem) => hasContent(stem)),
    ),
  ];
  if (stems.length === 0) return null;
  return stems.map((stem) => `"${stem}"*`).join(' OR ');
};

/* ------------------------------------------------------------- the statements */

/*
 * A trap these three statements avoid, and it is worth naming because it costs
 * an afternoon: `entry_search` is NEVER aliased. FTS5 takes the table's own
 * name on the left of MATCH and inside bm25 — given an alias it fails with
 * "no such column: s", at prepare time, which on a phone means the screen and
 * not CI. The side table is aliased freely; only the virtual one may not be.
 * Verified under SQLite 3.51.0 in search.sqlite.test.ts.
 */

/**
 * One business's records, as a predicate the reading statements can carry.
 *
 * `search_doc` holds no `tenant_id` and does not need one: it is keyed by
 * record id, so every LOOKUP through it is already exact. Only a business-wide
 * SEARCH needs scoping, and it gets it through the catalog — a dozen rows
 * reached by an index, which costs nothing next to the MATCH.
 *
 * Given `false` the statements come back exactly as they were before there was
 * more than one business on a phone, which is what keeps an un-scoped caller on
 * the code path it has always been on.
 */
const inTenant = (scoped: boolean, column: string): string =>
  scoped ? ` AND ${column} IN (SELECT id FROM record_catalog WHERE tenant_id = ?)` : '';

/**
 * The ranked search.
 *
 * `round(bm25(...))` then `day DESC` is the whole recency story, and it needs
 * the rounding to work: bm25 is near-continuous, so without it a hair's
 * difference in term frequency decides the order and the day never gets a say.
 * Rounded, it becomes coarse bands, and inside a band the RECENT entry wins —
 * which on a ledger is what a person means.
 *
 * bm25 is more negative for a better match, so ASC puts the best first.
 *
 * The weights are 4, 1, 2: a name beats a note word, and a hit reached through
 * a reference sits between the two, because it IS a name, just somebody else's.
 *
 * A null day sorts last under DESC in SQLite, which is where a dateless entry
 * belongs.
 *
 * `inRecord` narrows it to one record, for a section asked to show more than
 * the capped answer gave it. The same statement and the same ORDER BY, so the
 * rows a section already showed come back first and in the same order; bm25 is
 * scored over the whole index whatever the WHERE says, so the narrowing cannot
 * reorder them. The record id binds after the tenant.
 *
 * **It returns the WHOLE list row.** Not a summary of one: the version, the
 * pending flag, the link that wrote it, every column the entry list itself
 * selects. A search result is drawn by the entry list's OWN row component, so
 * it must be given the same row — otherwise a pending entry would be tappable
 * here and not there, and a link-written row would lose its badge.
 *
 * **The join to the mirror is doing two jobs.** It fetches the values, because a
 * result row is drawn exactly like an entry row and needs them. And being an
 * INNER join on the live predicate, it is a safety net: a document left behind
 * by drift can never produce a hit that opens nothing. A stale document costs a
 * missing result; a hit that opens nothing costs trust.
 */
export const searchSql = (scoped = false, inRecord = false): string => `
SELECT d.entry_id AS entryId,
       d.record_id AS recordId,
       d.day AS day,
       m.version AS version,
       m.values_json AS valuesJson,
       m.created_at AS createdAt,
       m.updated_at AS updatedAt,
       m.created_by AS createdBy,
       m.source_channel AS sourceChannel,
       m.link_id AS linkId,
       m.source_entry_id AS sourceEntryId,
       m.pending AS pending,
       m.signed_value AS signedValue
  FROM entry_search
  JOIN search_doc AS d ON d.doc_id = entry_search.rowid
  JOIN entry_mirror AS m ON m.entry_id = d.entry_id
                        AND m.is_current = 1
                        AND m.deleted_at IS NULL
 WHERE entry_search MATCH ?${inTenant(scoped, 'd.record_id')}${inRecord ? ' AND d.record_id = ?' : ''}
 ORDER BY round(bm25(entry_search, 4.0, 1.0, 2.0)) ASC, d.day DESC
 LIMIT ?`;

/**
 * How many entries of each record match, uncapped.
 *
 * Why it exists: the result list is capped at RESULT_LIMIT, and a section
 * heading that counted the rows it was GIVEN read "Labour wages 49" over a
 * worker with eighty-two wage rows — the cap, reported as a fact about the
 * shop. The heading has to say how many there ARE, so it is counted here, over
 * the same MATCH, the same tenant predicate and the same live join as
 * `searchSql`, which is what makes the two agree about what a match is.
 *
 * Cheap on purpose. No bm25, no ORDER BY, no LIMIT, and nothing read from the
 * mirror but the three columns of its liveness test — `values_json` is never
 * touched, so nothing is parsed. It is a count over the MATCH, grouped by the
 * side table's `record_id`.
 *
 * The join to the mirror stays, although a count needs no values: a document
 * left behind by drift is not a hit in `searchSql`, so it must not be a hit
 * here either, or the heading would promise a row the list can never show.
 */
export const matchCountSql = (scoped = false): string => `
SELECT d.record_id AS recordId,
       count(*) AS n
  FROM entry_search
  JOIN search_doc AS d ON d.doc_id = entry_search.rowid
  JOIN entry_mirror AS m ON m.entry_id = d.entry_id
                        AND m.is_current = 1
                        AND m.deleted_at IS NULL
 WHERE entry_search MATCH ?${inTenant(scoped, 'd.record_id')}
 GROUP BY d.record_id`;

/**
 * The unranked variant, for the fallback.
 *
 * No bm25 call at all: ordering a two-letter prefix by relevance is noise, and
 * skipping it means FTS5 need not compute a score for four hundred rows. Edit
 * distance decides the order, in JavaScript, over what comes back.
 */
export const looseSql = (scoped = false): string => `
SELECT d.entry_id AS entryId,
       d.record_id AS recordId,
       d.day AS day,
       m.version AS version,
       m.values_json AS valuesJson,
       m.created_at AS createdAt,
       m.updated_at AS updatedAt,
       m.created_by AS createdBy,
       m.source_channel AS sourceChannel,
       m.link_id AS linkId,
       m.source_entry_id AS sourceEntryId,
       m.pending AS pending,
       m.signed_value AS signedValue,
       entry_search.title AS title,
       entry_search.body AS body
  FROM entry_search
  JOIN search_doc AS d ON d.doc_id = entry_search.rowid
  JOIN entry_mirror AS m ON m.entry_id = d.entry_id
                        AND m.is_current = 1
                        AND m.deleted_at IS NULL
 WHERE entry_search MATCH ?${inTenant(scoped, 'd.record_id')}
 ORDER BY d.day DESC
 LIMIT ?`;

/** Step one of the reference step: the ids of the rows whose own name matched. */
export const seedSql = (scoped = false): string => `
SELECT d.entry_id AS entryId
  FROM entry_search
  JOIN search_doc AS d ON d.doc_id = entry_search.rowid
 WHERE entry_search MATCH ?${inTenant(scoped, 'd.record_id')}
 ORDER BY round(bm25(entry_search, 4.0, 1.0, 2.0)) ASC
 LIMIT ?`;

/**
 * The records whose own name matches. Twelve rows, so no index is needed.
 *
 * It reads the COUNT and the entry noun as well as the name, and that is not
 * decoration. A record row is a door, and a door has to say what is behind it:
 * a bare name and a chevron does not tell a shop that its eight sales are in
 * there, so the row reads "Sales - 8 sales" exactly as the home screen does.
 *
 * The shorter name first, so "Sales" leads "Scrap sales".
 */
export const recordMatchSql = (scoped = false): string => `
SELECT id,
       name,
       kind,
       entry_count AS entryCount,
       entry_noun_key AS entryNounKey
  FROM record_catalog
 WHERE name LIKE ? COLLATE NOCASE${scoped ? ' AND tenant_id = ?' : ''}
 ORDER BY length(name) ASC
 LIMIT ?`;

/** `?, ?, ?` for a bound IN list. Never a value, so nothing can be injected. */
const marks = (count: number): string => Array.from({ length: count }, () => '?').join(', ');

/**
 * Every live entry of the records whose NAME matched.
 *
 * Why this statement exists at all. Typing "sales" matches a RECORD, because no
 * sale has the word "sales" written inside it. Answering with only a door left
 * a person exactly where they started: they tapped through to the top of the
 * record and had to look for the thing again. So the entries come too.
 *
 * Newest first, and the ORDER BY is COPIED FROM the entry list's own
 * (`entry-filter.ts`) and must stay copied — a shop that searches its way into
 * a record and then opens it should not find the rows in a different order.
 *
 * It reads the mirror and not the index, because these rows matched nothing:
 * they are here because of the record they are in. The liveness test is the
 * list's own, so a tombstone can never appear.
 *
 * Scoped on the mirror's OWN `tenant_id` rather than through the catalog: this
 * is the one reading statement whose table carries the column, so the predicate
 * costs a comparison instead of a subquery.
 */
/**
 * The text clause a record's own filter ANDs into its WHERE.
 *
 * Two marks, in this order: the MATCH expression, then the record id. The
 * record id is bound a SECOND time here — the outer WHERE already binds it for
 * `record_id = ?` — because the index is device-wide and a subquery that did
 * not say which record would answer with another one's entries.
 *
 * **`entry_search` is never aliased.** Given one, SQLite fails with "no such
 * column" at PREPARE time, which on a phone is a blank screen rather than a red
 * build. `search_doc` may be aliased and is, as `d`.
 *
 * The column is bare `entry_id`, not `m.entry_id`: `compileEntryFilter` selects
 * `FROM entry_mirror` with no alias to bind to.
 */
export const entryTextSql = (): string =>
  `entry_id IN (SELECT d.entry_id
                  FROM entry_search
                  JOIN search_doc d ON d.doc_id = entry_search.rowid
                 WHERE entry_search MATCH ? AND d.record_id = ?)`;

export const recordEntriesSql = (count: number, scoped = false): string =>
  `SELECT entry_id AS entryId,
          record_id AS recordId,
          day AS day,
          version AS version,
          values_json AS valuesJson,
          created_at AS createdAt,
          updated_at AS updatedAt,
          created_by AS createdBy,
          source_channel AS sourceChannel,
          link_id AS linkId,
          source_entry_id AS sourceEntryId,
          pending AS pending,
          signed_value AS signedValue
     FROM entry_mirror
    WHERE record_id IN (${marks(count)})
      AND is_current = 1
      AND deleted_at IS NULL${scoped ? '\n      AND tenant_id = ?' : ''}
    ORDER BY day DESC, server_seq DESC, created_at DESC
    LIMIT ?`;

/**
 * How many live entries each of these records holds: the count for a record
 * whose NAME matched.
 *
 * Such a record brings EVERY live entry with it (`recordEntriesSql`), so its
 * true number of matches is simply how many it holds. Counted from the mirror
 * with the list's own liveness test rather than read from the catalog's
 * `entry_count`, because that column is the server's figure and a row saved
 * offline is on the page before the server has heard of it.
 */
export const recordLiveCountSql = (count: number, scoped = false): string =>
  `SELECT record_id AS recordId,
          count(*) AS n
     FROM entry_mirror
    WHERE record_id IN (${marks(count)})
      AND is_current = 1
      AND deleted_at IS NULL${scoped ? '\n      AND tenant_id = ?' : ''}
    GROUP BY record_id`;

/** What `recordMatchSql` takes: a contains pattern with the wildcards escaped. */
export const containsPattern = (typed: string): string => {
  const words = wordsIn(typed);
  const first = words[0] ?? '';
  return `%${first.replace(/[%_\\]/g, '')}%`;
};

/* --------------------------------------------------------- the typo fallback */

/**
 * How far apart two words are, as single-character edits.
 *
 * Two rows rather than a full matrix: the strings are words, and this runs once
 * per candidate word per typed word over up to four hundred candidates.
 * Compared by code point, so a Devanagari cluster counts as its characters and
 * not as its bytes.
 */
export const editDistance = (left: string, right: string): number => {
  const a = [...left];
  const b = [...right];
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_unused, at) => at);
  for (let i = 1; i <= a.length; i += 1) {
    const current: number[] = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitution = (previous[j - 1] ?? 0) + (a[i - 1] === b[j - 1] ? 0 : 1);
      const deletion = (previous[j] ?? 0) + 1;
      const insertion = (current[j - 1] ?? 0) + 1;
      current[j] = Math.min(substitution, deletion, insertion);
    }
    previous = current;
  }
  return previous[b.length] ?? 0;
};

/**
 * How many edits a word of this length is allowed to be wrong by.
 *
 * Nothing for one or two characters, because at that length every word is
 * within one edit of every other and the results would be noise. One for a
 * short word, two for a long one — the usual scale, and the reason a long name
 * survives a doubled letter and a swapped pair at once.
 */
export const typoBudget = (word: string): number => {
  const length = [...word].length;
  if (length <= 2) return 0;
  if (length <= 4) return 1;
  return 2;
};

/**
 * The closest any word of `text` comes to `word`.
 *
 * A prefix counts as a perfect match, because the strict query would have found
 * it: the fallback only ever runs when nothing matched, so a candidate whose
 * word merely STARTS with what was typed got here through a different word and
 * should not be penalised for this one.
 */
export const closeness = (word: string, text: string): number => {
  let best = Number.POSITIVE_INFINITY;
  for (const candidate of wordsIn(text)) {
    const lower = candidate.toLowerCase();
    const typed = word.toLowerCase();
    if (lower.startsWith(typed)) return 0;
    const distance = editDistance(typed, lower);
    if (distance < best) best = distance;
  }
  return best;
};

export type NearCandidate = {
  /** Everything the row holds that a person might have been aiming at. */
  readonly text: string;
};

/**
 * How wrong this candidate is, or null when it is not a near match at all.
 *
 * EVERY typed word must land within its own budget. One word matching out of
 * three is not a near miss, it is a different row.
 */
export const nearScore = (typed: string, text: string): number | null => {
  const words = wordsIn(typed);
  if (words.length === 0) return null;
  let total = 0;
  for (const word of words) {
    const distance = closeness(word, text);
    if (distance > typoBudget(word)) return null;
    total += distance;
  }
  return total;
};

/**
 * The candidates that are near misses, closest first.
 *
 * Stable within a score: the caller passes rows in day order, and `sort` in
 * every JavaScript engine this app runs on is stable, so the recent one of two
 * equally-close rows stays on top without a second key.
 */
export const nearest = <T extends NearCandidate>(typed: string, rows: readonly T[]): T[] => {
  const scored: { row: T; score: number }[] = [];
  for (const row of rows) {
    const score = nearScore(typed, row.text);
    if (score !== null) scored.push({ row, score });
  }
  scored.sort((left, right) => left.score - right.score);
  return scored.map((one) => one.row);
};

/* ------------------------------------------------------- keeping it in step */

/*
 * The write statements.
 *
 * Strings, like every other statement in this module, so `search.sqlite.test.ts`
 * can run the EXACT SQL the app runs. `entry-repo.ts` is the only file that
 * runs them, and it does so inside the same transaction as the mirror write —
 * which is the whole reason an entry and its document cannot come apart.
 */

/**
 * How many mirror rows one rebuild transaction handles.
 *
 * Small, because a chunk holds the JS thread from its first read to its commit,
 * and a tap that lands meanwhile waits for all of it. At five hundred a chunk
 * held the thread for 250 to 950 ms on the iOS simulator, and a tap on a record
 * waited that long.
 */
export const REBUILD_CHUNK = 100;

/**
 * How many row ids one read of a record's id list returns.
 *
 * The ids come from the index alone, but they still come sorted, and a sort
 * costs about the whole record each time. So they are read in blocks many
 * chunks long rather than once per chunk.
 */
export const REBUILD_ID_BLOCK = 2_000;

/** The words. Must be followed IMMEDIATELY by `linkDocSql`. */
export const insertDocSql = (): string =>
  'INSERT INTO entry_search(title, body, refs) VALUES (?, ?, ?)';

/**
 * The row that addresses those words.
 *
 * `last_insert_rowid()` and not a value read back into JavaScript, because
 * `INSERT ... RETURNING rowid` on an FTS5 virtual table answers -1 — verified,
 * and pinned in the sqlite test. The two statements must run back to back on
 * one connection with nothing between them; `writeDoc` is the only caller and
 * that is why it is one function.
 */
export const linkDocSql = (): string =>
  `INSERT INTO search_doc(doc_id, entry_id, record_id, day)
   VALUES (last_insert_rowid(), ?, ?, ?)`;

/**
 * Forget one entry's document. Words first, then the row that addresses them.
 *
 * The subselect is what makes this one statement rather than a read and a
 * write: FTS5 deletes by rowid natively, and `search_doc.entry_id` is UNIQUE,
 * so the plan is two index seeks. Safe to run when there is no document.
 */
export const deleteDocWordsSql = (): string =>
  'DELETE FROM entry_search WHERE rowid IN (SELECT doc_id FROM search_doc WHERE entry_id = ?)';

export const deleteDocRowSql = (): string => 'DELETE FROM search_doc WHERE entry_id = ?';

/**
 * The same, for a batch of entries. `count` is how many ids follow.
 *
 * The prune deletes rows a chunk at a time, and a statement per entry made the
 * documents cost more than the rows they belonged to: two statements per row,
 * outside any transaction, half a million times over. These are the same two
 * seeks, asked once for the whole chunk.
 */
export const deleteDocsWordsSql = (count: number): string =>
  `DELETE FROM entry_search WHERE rowid IN (
     SELECT doc_id FROM search_doc WHERE entry_id IN (${marks(count)}))`;

export const deleteDocsRowsSql = (count: number): string =>
  `DELETE FROM search_doc WHERE entry_id IN (${marks(count)})`;

/** The same, for every document of some records. `count` is how many ids follow. */
export const deleteRecordWordsSql = (count: number): string =>
  `DELETE FROM entry_search WHERE rowid IN (
     SELECT doc_id FROM search_doc WHERE record_id IN (${marks(count)}))`;

export const deleteRecordRowsSql = (count: number): string =>
  `DELETE FROM search_doc WHERE record_id IN (${marks(count)})`;

/** Sign-out. Both tables, nothing kept. */
export const clearWordsSql = (): string => 'DELETE FROM entry_search';
export const clearRowsSql = (): string => 'DELETE FROM search_doc';

/**
 * The rows of one record that must each have a document, one chunk at a time.
 *
 * The liveness test is the list's own: current, not deleted. A PENDING row is
 * included on purpose — it is on the screen, so a shop must be able to find it.
 *
 * Two steps, not one statement with an OFFSET. `ORDER BY rowid LIMIT ? OFFSET ?`
 * sorted every row of the record, `values_json` and all, to hand back five
 * hundred: on a record of 2.7 lakh rows one chunk read took up to 3.25 s. And
 * an OFFSET shifts when a row before it goes, so a page could skip a row.
 *
 * So the ids come first, from `entry_mirror_record_live` alone: its entries
 * hold the rowid, so no table page is read. They come in THAT index's order,
 * writer and then rowid, because any other order is a sort of the rest of the
 * record on every read: `ORDER BY rowid` alone cost 260 ms a read on the iOS
 * simulator, up to 950 ms. The cursor is a writer and a rowid, and one read
 * never crosses a writer:
 *
 * - `rebuildIdsSameWriterSql`: the rest of the cursor's writer. `IS ?` and not
 *   `= ?`, so that a row with no writer is one writer too. A seek on all
 *   three index columns and the rowid.
 * - `rebuildIdsNextWriterSql` / `rebuildIdsFirstWriterSql`: the first rows of
 *   the next writer, once the cursor's writer has none left. NULL sorts first,
 *   so after the NULL writer every writer is "next".
 *
 * A row-value `(created_by, rowid) > (?, ?)` reads as one statement, but the
 * planner seeks on `created_by` alone and walks the writer's rows up to the
 * rowid: 50 ms deep inside one writer against 0.1 ms for these.
 *
 * Then the rows of one chunk come by those ids, one seek each. The `+` keeps
 * the planner on the rowid seeks: without it, it may walk the record's index.
 */
const REBUILD_ID_COLUMNS = `SELECT rowid AS rowId, created_by AS createdBy
     FROM entry_mirror
    WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL`;

/** Params: record id, writer (or null), rowid, limit. */
export const rebuildIdsSameWriterSql = (): string =>
  `${REBUILD_ID_COLUMNS} AND created_by IS ? AND rowid > ?
    ORDER BY rowid
    LIMIT ?`;

/** Params: record id, writer, limit. The writers after a named one. */
export const rebuildIdsNextWriterSql = (): string =>
  `${REBUILD_ID_COLUMNS} AND created_by > ?
    ORDER BY created_by, rowid
    LIMIT ?`;

/** Params: record id, limit. The writers after the NULL one, which is all of them. */
export const rebuildIdsFirstWriterSql = (): string =>
  `${REBUILD_ID_COLUMNS} AND created_by IS NOT NULL
    ORDER BY created_by, rowid
    LIMIT ?`;

/** The rows of one chunk, by the ids the reads above gave. `count` ids, then the record id. */
export const rebuildRowsSql = (count: number): string =>
  `SELECT rowid AS rowId, entry_id AS entryId, day AS day, values_json AS valuesJson
     FROM entry_mirror
    WHERE rowid IN (${marks(count)})
      AND +record_id = ? AND +is_current = 1 AND deleted_at IS NULL
    ORDER BY rowid`;

/**
 * The live lines of one lines record under some entries, for those entries'
 * documents (docs/plans/entry-lines-and-layout.md, Decision 19). A line names
 * its root entry in its own values, so one read finds both levels' lines of a
 * record by the path of its root field.
 *
 * Params: the root field's JSON path (`$."<field id>"`), the lines record id,
 * the same path again, then `count` entry ids. Answers each line's root and
 * values, in the order the mirror holds them.
 */
export const linesUnderSql = (count: number): string =>
  `SELECT json_extract(values_json, ?) AS rootId, values_json AS valuesJson
     FROM entry_mirror
    WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL
      AND json_extract(values_json, ?) IN (${marks(count)})
    ORDER BY rowid`;

/** The JSON path `linesUnderSql` reads a root field by. A field id is a uuid, so it needs no escaping. */
export const rootPathOf = (fieldId: string): string => `$."${fieldId}"`;

/**
 * The two halves of the invariant that catches a forgotten write path.
 *
 * They must be equal for every record. A write path that forgets its document
 * makes an entry silently unfindable forever and crashes nothing, so this is
 * the one thing worth counting.
 */
export const docCountSql = (): string =>
  'SELECT count(*) AS n FROM search_doc WHERE record_id = ?';

export const liveCountSql = (): string =>
  `SELECT count(*) AS n FROM entry_mirror
    WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL`;

/**
 * The documents of a record whose entries are gone.
 *
 * A rebuild does NOT clear the record first, on purpose: clearing would make it
 * unsearchable for the seconds the rebuild takes, and a shop searching
 * mid-rebuild would be told its sales do not exist. Instead every document is
 * written over in place, and then this removes the ones left pointing at
 * nothing. It is also the repair for an orphan any drift left behind.
 */
export const deleteOrphanWordsSql = (): string =>
  `DELETE FROM entry_search WHERE rowid IN (
     SELECT doc_id FROM search_doc
      WHERE record_id = ?
        AND entry_id NOT IN (
          SELECT entry_id FROM entry_mirror
           WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL))`;

export const deleteOrphanRowsSql = (): string =>
  `DELETE FROM search_doc
    WHERE record_id = ?
      AND entry_id NOT IN (
        SELECT entry_id FROM entry_mirror
         WHERE record_id = ? AND is_current = 1 AND deleted_at IS NULL)`;

/**
 * The records whose index is missing or was built by an older renderer.
 *
 * One statement rather than a read of the catalogue and a read per record: the
 * stamp key is built in SQL from the record id, so a device holding a dozen
 * records answers this with one seek. Ordered by rowid so the home screen's
 * first record is indexed first, which is the one a person is most likely to
 * search.
 */
export const needsIndexSql = (): string =>
  `SELECT c.id AS recordId
     FROM record_catalog AS c
     LEFT JOIN sync_meta AS m ON m.key = 'search_ver:' || c.id
    WHERE m.value IS NULL OR m.value <> ?
    ORDER BY c.rowid`;

